import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import RouteMap from "../components/RouteMap";
import { API_BASE_URL} from "../constants/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return "N/A";
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  } catch { return String(val); }
};

// Converts a datetime string stored in America/Los_Angeles tz → IST (UTC+5:30)
// Output format: YYYY-MM-DD HH:mm:ss
const formatPushDate = (val) => {
  if (!val) return "N/A";
  try {
    const str = String(val).replace(" ", "T").replace(/Z$/, "");
    // Treat the stored value as UTC temporarily to do arithmetic
    const fakeUtcMs = new Date(str + "Z").getTime();
    if (isNaN(fakeUtcMs)) return String(val);
    // Find what LA clock shows for that "UTC" value, to measure the LA offset
    const laRepr = new Date(fakeUtcMs).toLocaleString("sv-SE", { timeZone: "America/Los_Angeles" });
    const laReprMs = new Date(laRepr.replace(" ", "T") + "Z").getTime();
    // offset = how many ms LA is behind UTC (e.g. 8h for PST, 7h for PDT)
    const offsetMs = fakeUtcMs - laReprMs;
    // Shift fake-UTC back by the LA offset to get true UTC, then show in IST
    const trueUtcMs = fakeUtcMs + offsetMs;
    return new Date(trueUtcMs).toLocaleString("sv-SE", { timeZone: "Asia/Kolkata" });
  } catch { return String(val); }
};

const formatValue = (val, unit) => (val ? `${val} ${unit || ""}`.trim() : "N/A");

const humanLabel = (key) =>
    key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim();

const downloadCsv = (sessions) => {
  const headers = [
    "IP Address", "Session Start", "Session Stop", "Duration", "Disc. Code",
    "Disc. Reason", "In Packets", "Out Packets", "Called Station", "Status",
  ];
  const rows = sessions.map((s) =>
      [
        s.framedIPAddress, formatDate(s.sessionStartTimestamp),
        formatDate(s.sessionStopTimestamp), s.sessionDuration,
        s.disconnectionCauseCode, s.disconnectionReason,
        s.acctInputPackets, s.acctOutputPackets, s.calledStationId, s.acctStatusType,
      ].map((v) => v ?? "N/A")
  );
  const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = "session_data.csv"; a.click();
  URL.revokeObjectURL(url);
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const S = {
  surface: "#1c1c1c",
  card:    "#161616",
  border:  "#2a2a2a",
  muted:   "#888888",
  accent:  "#ffc20e",
  red:     "#ef4444",
  green:   "#22c55e",
};

const btnStyle = (active) => ({
  padding: "9px 22px", fontSize: 13, fontWeight: 600, borderRadius: 12,
  cursor: "pointer", transition: "all 0.15s",
  backgroundColor: active ? S.accent : "transparent",
  color:           active ? "#000"   : S.accent,
  border: `1px solid ${S.accent}`,
});

// ─── Reusable atoms ───────────────────────────────────────────────────────────
const InfoCard = ({ label, value }) => (
    <div style={{ backgroundColor: S.surface, border: `1px solid ${S.border}`,
      borderRadius: 12, padding: "14px 18px" }}>
      <p style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.07em", margin: "0 0 4px" }}>{label}</p>
      <p style={{ color: "#fff", fontSize: 13, fontWeight: 600, margin: 0,
        wordBreak: "break-all" }}>{value ?? "N/A"}</p>
    </div>
);

const StatCard = ({ label, value }) => (
    <div style={{ backgroundColor: S.card, border: `1px solid ${S.border}`,
      borderRadius: 16, padding: "18px 12px", textAlign: "center" }}>
      <p style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.07em", margin: "0 0 6px" }}>{label}</p>
      <p style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: 0 }}>{value}</p>
    </div>
);

const TH = ({ children }) => (
    <th style={{ padding: "10px 16px", textAlign: "left", fontSize: 11,
      textTransform: "uppercase", color: S.muted, fontWeight: 600,
      letterSpacing: "0.05em", whiteSpace: "nowrap",
      backgroundColor: "#2a2a2a", borderBottom: `1px solid ${S.border}` }}>
      {children}
    </th>
);

const TD = ({ children, yellow }) => (
    <td style={{ padding: "11px 16px", color: yellow ? "#fbbf24" : "#d1d5db",
      borderBottom: "1px solid #1e1e1e", whiteSpace: "nowrap" }}>
      {children ?? "N/A"}
    </td>
);

// ─── Trip Detail Drawer ───────────────────────────────────────────────────────
// Calls the same /api/tvsm/trips endpoint with vehicleId (from tcuData.vehicleid)
// + trip's own startDatetime/endDatetime to fetch GPS trace.
// Change reportType below to whatever value your backend expects for GPS points.
const TripDetailDrawer = ({ trip, vehicleId, orgId, onClose }) => {
  const [routePoints,  setRoutePoints]  = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError,   setRouteError]   = useState(null);

  // const rows = Object.entries(trip).map(([k, v]) => ({
  //   label: humanLabel(k),
  //   value: typeof v === "object" && v !== null
  //       ? JSON.stringify(v)
  //       : String(v ?? "N/A"),
  // }));
  const rows = Object.entries(trip)
      .filter(([k]) => {
        const key = k.toLowerCase().replace(/\s+/g, "");
        return !(
            key.includes("lat") ||
            key.includes("latitude") ||
            key.includes("lng") ||
            key.includes("longitude")
        );
      })
      .map(([k, v]) => ({
        label: humanLabel(k),
        value:
            typeof v === "object" && v !== null
                ? JSON.stringify(v)
                : String(v ?? "N/A"),
      }));

    useEffect(() => {
        const lats = trip.vLatitude || trip.VLatitude || [];
        const lngs = trip.vLongitude || trip.VLongitude || [];

        const pts = lats
            .map((lat, i) => ({
                lat: parseFloat(lat),
                lng: parseFloat(lngs[i]),
                datetime: i === 0
                    ? (trip.start_datetime || trip.startDatetime || "Start")
                    : i === lats.length - 1
                        ? (trip.end_datetime || trip.endDatetime || "End")
                        : null,
            }))
            .filter(p => !isNaN(p.lat) && !isNaN(p.lng));

        if (pts.length) {
            setRoutePoints(pts);
        } else {
            setRouteError("No coordinates found for this trip.");
            setRoutePoints([]);
        }
    }, [trip]);
  return (
      <div
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          style={{ position: "fixed", inset: 0, zIndex: 200,
            backgroundColor: "rgba(0,0,0,0.72)", display: "flex" }}>

        <div style={{ marginLeft: "auto", width: "min(96vw, 1100px)", height: "100%",
          backgroundColor: "#111", display: "flex", flexDirection: "column",
          borderLeft: `1px solid ${S.border}`, overflow: "hidden" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 20px", backgroundColor: S.surface,
            borderBottom: `1px solid ${S.border}`, flexShrink: 0 }}>
            <div>
              <p style={{ color: S.accent, fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>
                Trip Details
              </p>
              <p style={{ color: "#fff", fontSize: 14, fontWeight: 600, margin: "3px 0 0" }}>
                {formatDate(trip.start_datetime)}&nbsp;→&nbsp;{formatDate(trip.end_datetime)}
              </p>
            </div>
            <button onClick={onClose}
                    style={{ background: "transparent", border: "1px solid #333",
                      color: "#aaa", borderRadius: 8, padding: "6px 14px",
                      fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              ✕ Close
            </button>
          </div>

          {/* Two-column body */}
          <div style={{ flex: 1, display: "grid",
            gridTemplateColumns: "300px 1fr", overflow: "hidden" }}>

            {/* Left — all trip properties, scrollable */}
            <div style={{ overflowY: "auto", padding: "16px 20px",
              borderRight: `1px solid ${S.border}` }}>
              <p style={{ color: S.muted, fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 10px" }}>
                Properties
              </p>
              {rows.map(({ label, value }) => (
                  <div key={label}
                       style={{ display: "flex", justifyContent: "space-between",
                         alignItems: "flex-start", gap: 12, padding: "8px 0",
                         borderBottom: "1px solid #1e1e1e" }}>
                <span style={{ color: "#666", fontSize: 11, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.05em",
                  flexShrink: 0, minWidth: 90, paddingTop: 1 }}>
                  {label}
                </span>
                    <span style={{ color: "#e5e7eb", fontSize: 12, fontWeight: 500,
                      textAlign: "right", wordBreak: "break-word", maxWidth: 170 }}>
                  {value}
                </span>
                  </div>
              ))}
            </div>

            {/* Right — Leaflet map */}
            <div style={{ position: "relative", overflow: "hidden",
              backgroundColor: "#0d0d0d" }}>

              {/* Legend */}
              <div style={{ position: "absolute", top: 12, left: 12, zIndex: 500,
                backgroundColor: "rgba(17,17,17,0.9)", borderRadius: 8,
                padding: "8px 12px", border: `1px solid ${S.border}` }}>
                {[{ color: S.green, label: "Start" }, { color: S.red, label: "End" }].map(({ color, label }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center",
                      gap: 7, marginBottom: 5 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%",
                        backgroundColor: color, flexShrink: 0 }} />
                      <span style={{ color: "#ccc", fontSize: 11 }}>{label}</span>
                    </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 18, height: 3, backgroundColor: "#3b82f6",
                        borderRadius: 2, flexShrink: 0 }} />
                  <span style={{ color: "#ccc", fontSize: 11 }}>Route</span>
                </div>
              </div>

              {routeLoading && (
                  <div style={{ position: "absolute", inset: 0, zIndex: 490,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: "#111" }}>
                    <span style={{ color: S.muted, fontSize: 13 }}>Loading map…</span>
                  </div>
              )}

              {routeError && !routeLoading && (
                  <div style={{ position: "absolute", top: 12, right: 12, zIndex: 490,
                    backgroundColor: "rgba(239,68,68,0.15)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    borderRadius: 8, padding: "5px 10px" }}>
                    <span style={{ color: "#fca5a5", fontSize: 11 }}>{routeError}</span>
                  </div>
              )}

              <RouteMap routePoints={routePoints ?? []} />
            </div>
          </div>
        </div>
      </div>
  );
};

// ─── Date Range Modal ─────────────────────────────────────────────────────────
const DateRangeModal = ({ tripsLoading, tripsError, onCancel, onSubmit }) => {
  // const startRef = useRef(null);
  // const endRef = useRef(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [err, setErr] = useState(null);

  const handleChange = (e) => {
    console.log("hello")
    console.log(e.target.value)

  }

  const handleSubmit = (e) => {
    e.preventDefault();
    // const startVal = startRef.current?.value;
    // const endVal = endRef.current?.value;
    //
    console.log("Start value:", startDate);
    console.log("End value:", endDate);

    if (!startDate || !endDate) {
      setErr("Please select both start and end date.");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setErr("Please enter valid date and time.");
      return;
    }

    if (start >= end) {
      setErr("End must be after start.");
      return;
    }

    setErr(null);
    onSubmit(startDate, endDate);
  };

  const inputSt = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13,
    backgroundColor: "#0d0d0d",
    border: `1px solid ${S.border}`,
    color: "#fff",
    boxSizing: "border-box",
    colorScheme: "dark",
    outline: "none",
  };

  const labelSt = {
    display: "block",
    color: S.muted,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 6,
  };

  return (
      <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 150,
            backgroundColor: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
      >
        <form
            onSubmit={handleSubmit}
            noValidate
            style={{
              backgroundColor: S.surface,
              border: `1px solid ${S.border}`,
              borderRadius: 18,
              padding: 24,
              width: "min(94vw, 400px)",
            }}
        >
          <p
              style={{
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                margin: "0 0 20px",
              }}
          >
            Select date range
          </p>

          {/* START */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelSt}>Start date &amp; time</label>
            <input
                // ref={startRef}
                type="date"
                style={inputSt}
                value ={startDate}
                onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* END */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelSt}>End date &amp; time</label>
            <input
                // ref={endRef}
                type="date"
                style={inputSt}
                value ={endDate}
                onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          {(err || tripsError) && (
              <p style={{ color: S.red, fontSize: 12, margin: "0 0 14px" }}>
                {err || tripsError}
              </p>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button
                type="button"
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  backgroundColor: "transparent",
                  border: `1px solid ${S.border}`,
                  color: "#aaa",
                  cursor: "pointer",
                }}
            >
              Cancel
            </button>

            <button
                type="submit"
                disabled={tripsLoading}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  backgroundColor: S.accent,
                  color: "#000",
                  border: "none",
                  cursor: tripsLoading ? "not-allowed" : "pointer",
                  opacity: tripsLoading ? 0.6 : 1,
                }}
            >
              {tripsLoading ? "Fetching…" : "Fetch"}
            </button>
          </div>
        </form>
      </div>
  );
};
// ─── Main page ────────────────────────────────────────────────────────────────
const DataDiagnoseResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePanel, setActivePanel] = useState(null);

  // Session
  const [sessions,        setSessions]        = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError,   setSessionsError]   = useState(null);
  const [sessionsFetched, setSessionsFetched] = useState(false);

  // Data Usage
  const [dataUsage,        setDataUsage]        = useState(null);
  const [dataUsageLoading, setDataUsageLoading] = useState(false);
  const [dataUsageError,   setDataUsageError]   = useState(null);

  // Trips
  const [trips,         setTrips]         = useState([]);
  const [tripsLoading,  setTripsLoading]  = useState(false);
  const [tripsError,    setTripsError]    = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);

  //Charge
    // Charge
    const [charge, setCharge] = useState([]);
    const [chargeLoading, setChargeLoading] = useState(false);
    const [chargeError, setChargeError] = useState(null);
    const [chargeMode, setChargeMode] = useState(false);

  // Push Notifications
  const [pushData,     setPushData]     = useState(null);
  const [pushLoading,  setPushLoading]  = useState(false);
  const [pushError,    setPushError]    = useState(null);
  const [pushFetched,  setPushFetched]  = useState(false);
  const [gcmToken,     setGcmToken]     = useState(null);   // token shown in modal
  const [tokenCopied,  setTokenCopied]  = useState(false);

  // Trip detail drawer
  const [selectedTrip, setSelectedTrip] = useState(null);

  const state    = location.state;
  const response = state?.data;

  if (!response) {
    return (
        <Layout title="Device Diagnose Results">
          <p style={{ color: S.muted }}>
            No data found.{" "}
            <button onClick={() => navigate(-1)}
                    style={{ color: S.accent, background: "none", border: "none",
                      cursor: "pointer", textDecoration: "underline" }}>
              Go back
            </button>
          </p>
        </Layout>
    );
  }

  const tcuData = response?.tcuData || {};
  const source  = response?.source;

  // vehicleId is already in tcuData — no extra API call required
  const vehicleId = tcuData?.vehicleid;
  const orgId = tcuData?.orgid;

  if (!sessionsFetched && !source && (response?.data?.data || response?.data?.sessions)) {
    setSessions(response?.data?.data || response?.data?.sessions || []);
    setSessionsFetched(true);
  }

  // ── Session ────────────────────────────────────────────────────────────────
  const handleSessionClick = async () => {
    const closing = activePanel === "session";
    setActivePanel(closing ? null : "session");
    if (closing || sessionsLoading || sessionsFetched) return;
    const mobileNo = tcuData?.mobileNo;
    if (!mobileNo || mobileNo === "N/A") {
      setSessionsError("No valid mobile number to fetch session data.");
      setSessionsFetched(true);
      return;
    }
    try {
      setSessionsLoading(true); setSessionsError(null);
      const res  = await fetch(`${API_BASE_URL}/device/sessions?mobileNo=${encodeURIComponent(mobileNo)}`);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      const data = json?.data?.data || json?.data?.sessions || [];
      setSessions(data); setSessionsFetched(true);
      if (!data.length) setSessionsError("No session data found for this device.");
    } catch {
      setSessionsError("Failed to load session data. Please try again.");
      setSessionsFetched(true);
    } finally { setSessionsLoading(false); }
  };

  // ── Data Usage ─────────────────────────────────────────────────────────────
  const fetchDataUsage = async () => {
    const mobileNo = tcuData?.mobileNo;
    const simNo    = tcuData?.ccid;
    if (!mobileNo || mobileNo === "N/A" || !simNo) {
      setDataUsageError("Missing mobileNo or CCID."); return;
    }
    try {
      setDataUsageLoading(true); setDataUsageError(null);
      const res = await fetch(
          `${API_BASE_URL}/sim/usage?mobileNo=${encodeURIComponent(mobileNo)}&simNo=${encodeURIComponent(simNo)}`
      );
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      setDataUsage(await res.json());
    } catch { setDataUsageError("Failed to load data usage. Please try again."); }
    finally { setDataUsageLoading(false); }
  };

  const handleDataUsageClick = async () => {
    const closing = activePanel === "dataUsage";
    setActivePanel(closing ? null : "dataUsage");
    if (closing || dataUsage || dataUsageLoading) return;
    await fetchDataUsage();
  };

    // ── Trips ──────────────────────────────────────────────────────────────────
    const handleTripsClick = () => {
        setTripsError(null);
        setShowDateModal(true);
    };

    const handleFetchTrips = async (startDateTime, endDateTime) => {

        console.log("vehcleid = " + vehicleId);
        console.log("orgid = " + orgId);

        if (!vehicleId) {
            setTripsError("Vehicle ID missing from device data.");
            return;
        }

        const formatStart = (dateStr) => {
            const d = new Date(`${dateStr}T00:00:00`);
            return d.toISOString().slice(0,19).replace("T"," ");
        };

        const formatEnd = (dateStr) => {
            const d = new Date(`${dateStr}T23:59:59`);
            return d.toISOString().slice(0,19).replace("T"," ");
        };

        try {
            setTripsLoading(true);
            setTripsError(null);

            const res = await fetch(`${API_BASE_URL}/api/tvsm/trips`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    vehicleId: vehicleId,
                    orgId: orgId,
                    startDateTime: formatStart(startDateTime),
                    endDateTime: formatEnd(endDateTime),
                }),
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);

            const json = await res.json();
            console.log("TRIPS API RESPONSE:", JSON.stringify(json, null, 2));

            const list = json.trips || [];

            setTrips(list);
            setShowDateModal(false);
            setActivePanel("trips");

            if (!list.length) {
                setTripsError("No trips found for the selected range.");
            }

        } catch (err) {
            setTripsError(err.message || "Failed to fetch trips.");
        } finally {
            setTripsLoading(false);
        }
    };


// ── Charge ─────────────────────────────────────────────────────────────
    const handleChargeClick = () => {
        setChargeMode(true);
        setChargeError(null);
        setShowDateModal(true);
    };

    const handleFetchCharge = async (startDateTime, endDateTime) => {

        console.log("vehcleid = " + vehicleId);
        console.log("orgid = " + orgId);

        if (!vehicleId) {
            setChargeError("Vehicle ID missing from device data.");
            return;
        }

        const formatStart = (dateStr) => {
            const d = new Date(`${dateStr}T00:00:00`);
            return d.toISOString().slice(0,19).replace("T"," ");
        };

        const formatEnd = (dateStr) => {
            const d = new Date(`${dateStr}T23:59:59`);
            return d.toISOString().slice(0,19).replace("T"," ");
        };

        try {
            setChargeLoading(true);
            setChargeError(null);

            const res = await fetch(`${API_BASE_URL}/api/tvsm/charges`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    vehicleId: vehicleId,
                    orgId: orgId,
                    startDateTime: formatStart(startDateTime),
                    endDateTime: formatEnd(endDateTime),
                }),
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);


            const json = await res.json();
            console.log("CHARGE API RESPONSE:", json);

            const list = json.chargeSessions || [];

            setCharge(list);
            setShowDateModal(false);
            setActivePanel("charge");

            if (!list.length) {
                setChargeError("No charge found for the selected range.");
            }

        } catch (err) {
            setChargeError(err.message || "Failed to fetch charge.");
        } finally {
            setChargeLoading(false);
        }
    };

    // ── Push Notifications ──────────────────────────────────────────────────────
    const handlePushNotificationsClick = async () => {
        const closing = activePanel === "pushNotifications";
        setActivePanel(closing ? null : "pushNotifications");
        if (closing || pushLoading || pushFetched) return;
        const unitId = tcuData?.unitid;
        if (!unitId) {
            setPushError("TCU ID missing from device data.");
            setPushFetched(true);
            return;
        }
        try {
            setPushLoading(true);
            setPushError(null);
            const res = await fetch(`${API_BASE_URL}/device/push-notifications?unitId=${encodeURIComponent(unitId)}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            setPushData(json);
            setPushFetched(true);
        } catch (err) {
            setPushError(err.message || "Failed to load push notifications.");
            setPushFetched(true);
        } finally {
            setPushLoading(false);
        }
    };


    return (
        <Layout title="Device Diagnose Results">

            {/* TCU Info Grid */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 14,
                marginBottom: 28
            }}>
                <InfoCard label="TCU ID" value={tcuData?.unitid} />
                <InfoCard label="Vehicle ID" value={tcuData?.vehicleid} />
                <InfoCard label="CCID" value={tcuData?.ccid} />
                <InfoCard label="Mobile No" value={tcuData?.mobileNo} />
                <InfoCard label="Org Name" value={tcuData?.orgName} />
                <InfoCard label="Firmware" value={tcuData?.factory_firmware} />
                <InfoCard label="SoC" value={tcuData?.soc} />
                <InfoCard label="Odometer" value={tcuData?.odometer} />
                <InfoCard label="Datetime Package" value={formatDate(tcuData?.datetimePkt)} />
                <InfoCard label="GPS Received" value={formatDate(tcuData?.gpsRecvd)} />
                <InfoCard
                    label="Device Activation Date"
                    value={formatDate(tcuData?.vehicleGpsMappingStartDate)}
                />
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
                <button onClick={handleSessionClick} style={btnStyle(activePanel === "session")}>
                    {sessionsLoading ? "Loading…" : "Session Data"}
                </button>

                <button onClick={handleDataUsageClick} style={btnStyle(activePanel === "dataUsage")}>
                    {dataUsageLoading ? "Loading…" : "Data Usage"}
                </button>

                <button onClick={handleTripsClick} style={btnStyle(activePanel === "trips")}>
                    {tripsLoading ? "Loading…" : "Fetch Trips"}
                </button>

                <button onClick={handleChargeClick} style={btnStyle(activePanel === "charge")}>
                    {chargeLoading ? "Loading…" : "Fetch Charge"}
                </button>

                <button onClick={handlePushNotificationsClick} style={btnStyle(activePanel === "pushNotifications")}>
                    {pushLoading ? "Loading…" : "Push Notifications"}
                </button>
            </div>


            {/* ── Charge Panel ────────────────────────────────────────────────────── */}
            {activePanel === "charge" && (
                <div
                    style={{
                        borderRadius: 16,
                        overflow: "hidden",
                        border: `1px solid ${S.border}`,
                        marginBottom: 24,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 22px",
                            backgroundColor: S.surface,
                            borderBottom: `1px solid ${S.border}`,
                        }}
                    >
                        <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                            Charge History
                        </h3>

                        <button
                            onClick={handleChargeClick}
                            style={{
                                backgroundColor: "transparent",
                                border: `1px solid ${S.accent}`,
                                color: S.accent,
                                fontSize: 12,
                                fontWeight: 600,
                                padding: "5px 14px",
                                borderRadius: 8,
                                cursor: "pointer",
                            }}
                        >
                            ↺ New Range
                        </button>
                    </div>

                    {chargeError && (
                        <p style={{
                            color: "#f87171",
                            textAlign: "center",
                            padding: "24px 0",
                            fontSize: 13
                        }}>
                            {chargeError}
                        </p>
                    )}

                    {!chargeError && (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{
                                width: "100%",
                                fontSize: 13,
                                backgroundColor: S.card,
                                borderCollapse: "collapse"
                            }}>
                                <thead>
                                <tr>
                                    {[
                                        "#",
                                        "Start Time",
                                        "End Time",
                                        "Start SOC",
                                        "End SOC",
                                        "Energy Consumed"
                                    ].map((h) => (
                                        <TH key={h}>{h}</TH>
                                    ))}
                                </tr>
                                </thead>

                                <tbody>
                                {charge.length ? (
                                    charge.map((c, i) => (
                                        <tr key={i}>
                                            <TD>{i + 1}</TD>
                                            <TD>{formatDate(c.start_datetime)}</TD>
                                            <TD>{formatDate(c.end_datetime)}</TD>
                                            <TD>{c.start_soc}</TD>
                                            <TD>{c.end_soc}</TD>
                                            <TD>{c.energy_consumed}</TD>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{
                                            textAlign: "center",
                                            padding: "24px 0",
                                            color: "#6b7280",
                                            fontSize: 13
                                        }}>
                                            No charge sessions found
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
      {/* ── Session Panel ────────────────────────────────────────────────────── */}
        {activePanel === "session" && (
            <div style={{ borderRadius: 16, overflow: "hidden",
              border: `1px solid ${S.border}`, marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center",
                justifyContent: "space-between", padding: "14px 22px",
                backgroundColor: S.surface, borderBottom: `1px solid ${S.border}` }}>
                <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                  Session Details
                </h3>
                <button onClick={() => downloadCsv(sessions)} disabled={!sessions.length}
                        style={{ backgroundColor: S.accent, color: "#000", border: "none",
                          padding: "5px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8,
                          cursor: sessions.length ? "pointer" : "not-allowed",
                          opacity: sessions.length ? 1 : 0.4 }}>
                  Download CSV
                </button>
              </div>
              {sessionsLoading && (
                  <p style={{ color: "#9ca3af", textAlign: "center",
                    padding: "24px 0", fontSize: 13 }}>Loading…</p>
              )}
              {sessionsError && !sessionsLoading && (
                  <p style={{ color: "#f87171", textAlign: "center",
                    padding: "24px 0", fontSize: 13 }}>{sessionsError}</p>
              )}
              {!sessionsLoading && !sessionsError && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", fontSize: 13,
                      backgroundColor: S.card, borderCollapse: "collapse" }}>
                      <thead>
                      <tr>
                        {["IP Address","Session Start","Session Stop","Duration","Disc. Code",
                          "Disc. Reason","In Packets","Out Packets","Called Station","Status"].map((h) => (
                            <TH key={h}>{h}</TH>
                        ))}
                      </tr>
                      </thead>
                      <tbody>
                      {sessions.length ? sessions.map((s, i) => (
                          <tr key={i}>
                            <TD>{s.framedIPAddress}</TD>
                            <TD>{formatDate(s.sessionStartTimestamp)}</TD>
                            <TD>{formatDate(s.sessionStopTimestamp)}</TD>
                            <TD>{s.sessionDuration}</TD>
                            <TD>{s.disconnectionCauseCode}</TD>
                            <TD>{s.disconnectionReason}</TD>
                            <TD>{s.acctInputPackets}</TD>
                            <TD>{s.acctOutputPackets}</TD>
                            <TD>{s.calledStationId}</TD>
                            <TD yellow>{s.acctStatusType}</TD>
                          </tr>
                      )) : (
                          <tr>
                            <td colSpan="10" style={{ textAlign: "center",
                              padding: "24px 0", color: "#6b7280", fontSize: 13 }}>
                              No session data found
                            </td>
                          </tr>
                      )}
                      </tbody>
                    </table>
                  </div>
              )}
            </div>
        )}

        {/* ── Data Usage Panel ─────────────────────────────────────────────────── */}
        {activePanel === "dataUsage" && (
            <div style={{ borderRadius: 16, padding: 24, marginBottom: 24,
              backgroundColor: S.surface, border: `1px solid ${S.border}` }}>
              <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>
                Data Usage
              </h3>
              {dataUsageLoading && (
                  <p style={{ color: "#9ca3af", textAlign: "center",
                    padding: "24px 0", fontSize: 13 }}>Loading…</p>
              )}
              {dataUsageError && !dataUsageLoading && (
                  <div style={{ textAlign: "center", padding: "24px 0" }}>
                    <p style={{ color: "#f87171", fontSize: 13, marginBottom: 12 }}>
                      {dataUsageError}
                    </p>
                    <button
                        onClick={() => { setDataUsage(null); setDataUsageError(null); fetchDataUsage(); }}
                        style={{ backgroundColor: S.accent, color: "#000", border: "none",
                          padding: "8px 18px", fontSize: 12, fontWeight: 600,
                          borderRadius: 8, cursor: "pointer" }}>
                      Retry
                    </button>
                  </div>
              )}
              {!dataUsageLoading && !dataUsageError && dataUsage && (() => {
                const u = dataUsage?.data || dataUsage || {};
                return (
                    <>
                      <div style={{ borderRadius: 12, padding: "12px 18px", marginBottom: 18,
                        textAlign: "center", backgroundColor: "rgba(255,194,14,0.1)",
                        border: "1px solid rgba(255,194,14,0.3)" }}>
                        <p style={{ color: "#fbbf24", fontSize: 10, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.07em", margin: 0 }}>Plan</p>
                        <p style={{ color: "#fbbf24", fontSize: 18, fontWeight: 700,
                          margin: "4px 0 0" }}>{u.planName || "N/A"}</p>
                      </div>
                      <div style={{ display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                        <StatCard label="Used"
                                  value={formatValue(u.usedDataUsage, u.dataUsageUnits)} />
                        <StatCard label="Available"
                                  value={formatValue(u.remainingDataUsage, u.dataUsageUnits)} />
                        <StatCard label="Allocated"
                                  value={formatValue(u.totalDataUsage, u.dataUsageUnits)} />
                      </div>
                    </>
                );
              })()}
              {!dataUsageLoading && !dataUsageError && !dataUsage && (
                  <p style={{ color: "#6b7280", textAlign: "center",
                    padding: "24px 0", fontSize: 13 }}>No data usage available.</p>
              )}
            </div>
        )}

        {/* ── Trips Panel ──────────────────────────────────────────────────────── */}
        {activePanel === "trips" && (
            <div style={{ borderRadius: 16, overflow: "hidden",
              border: `1px solid ${S.border}`, marginBottom: 24 }}>

              <div style={{ display: "flex", alignItems: "center",
                justifyContent: "space-between", padding: "14px 22px",
                backgroundColor: S.surface, borderBottom: `1px solid ${S.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                    Trip History
                  </h3>
                  <span style={{ backgroundColor: "rgba(255,194,14,0.15)",
                    color: S.accent, fontSize: 11, fontWeight: 700,
                    padding: "3px 10px", borderRadius: 6 }}>
                {trips.length} trip{trips.length !== 1 ? "s" : ""}
              </span>
                </div>
                <button onClick={handleTripsClick}
                        style={{ backgroundColor: "transparent", border: `1px solid ${S.accent}`,
                          color: S.accent, fontSize: 12, fontWeight: 600,
                          padding: "5px 14px", borderRadius: 8, cursor: "pointer" }}>
                  ↺ New Range
                </button>
              </div>

              {tripsError && (
                  <p style={{ color: "#f87171", textAlign: "center",
                    padding: "24px 0", fontSize: 13 }}>{tripsError}</p>
              )}

              {!tripsError && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", fontSize: 13,
                      backgroundColor: S.card, borderCollapse: "collapse" }}>
                      <thead>
                      <tr>
                        {["#","Start Time","End Time","Distance (km)","Start SOC","End SOC",
                          "Start Odometer","End Odometer","Avg Speed","Max Speed","Start Location","End Location"].map((h) => (
                            <TH key={h}>{h}</TH>
                        ))}
                      </tr>
                      </thead>
                      <tbody>
                      {trips.length ? trips.map((t, i) => (
                          <tr key={i}
                              onClick={() => setSelectedTrip(t)}
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1a1a1a")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                            <td style={{ padding: "11px 16px",
                              borderBottom: "1px solid #1e1e1e" }}>
                        <span style={{ color: S.accent, fontSize: 11, fontWeight: 700 }}>
                          {i + 1}
                        </span>
                            </td>
                            <TD>{formatDate(t.start_datetime)}</TD>
                            <TD>{formatDate(t.end_datetime)}</TD>
                            <TD>{t.distance}</TD>
                            <TD>{t.start_soc}</TD>
                            <TD>{t.end_soc}</TD>
                            <TD>{t.start_odometer}</TD>
                            <TD>{t.end_odometer}</TD>
                            <TD>{t.avg_speed}</TD>
                            <TD>{t.max_speed}</TD>
                            <TD>{t.start_location}</TD>
                            <TD>{t.end_location}</TD>
                          </tr>
                      )) : (
                          <tr>
                            <td colSpan="12" style={{ textAlign: "center",
                              padding: "24px 0", color: "#6b7280", fontSize: 13 }}>
                              No trips found
                            </td>
                          </tr>
                      )}
                      </tbody>
                    </table>
                  </div>
              )}

              {trips.length > 0 && (
                  <p style={{ color: "#444", fontSize: 11,
                    textAlign: "center", padding: "10px 0" }}>
                    Click any row to view full details &amp; route map
                  </p>
              )}
            </div>
        )}
        {/* ── Push Notifications Panel ──────────────────────────────────────────── */}
        {activePanel === "pushNotifications" && (
            <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${S.border}`, marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 22px", backgroundColor: S.surface, borderBottom: `1px solid ${S.border}` }}>
                    <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                        Push Notifications
                    </h3>
                </div>

                {pushLoading && (
                    <p style={{ color: "#9ca3af", textAlign: "center", padding: "24px 0", fontSize: 13 }}>
                        Loading…
                    </p>
                )}

                {pushError && !pushLoading && (
                    <p style={{ color: "#f87171", textAlign: "center", padding: "24px 0", fontSize: 13 }}>
                        {pushError}
                    </p>
                )}

                {!pushLoading && !pushError && pushData && (
                    <div style={{ backgroundColor: S.card }}>
                        {/* Registered Phones */}
                        <div style={{ padding: "14px 22px 8px", borderBottom: `1px solid ${S.border}` }}>
                            <p style={{ color: S.muted, fontSize: 11, fontWeight: 700,
                                textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 10px" }}>
                                Registered Phones
                            </p>
                            {pushData.phones?.length > 0 ? (
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                                        <thead>
                                        <tr>
                                            {["Phone ID", "OS", "Model", "Manufacturer", "OS Version", "User ID", "Token"].map((h) => (
                                                <TH key={h}>{h}</TH>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {pushData.phones.map((p, i) => (
                                            <tr key={i}>
                                                <TD yellow>{p.phoneid}</TD>
                                                <TD>{p.os}</TD>
                                                <TD>{p.model}</TD>
                                                <TD>{p.manufacturer}</TD>
                                                <TD>{p.osversion}</TD>
                                                <TD>{p.userid}</TD>
                                                <td style={{ padding: "11px 16px", borderBottom: "1px solid #1e1e1e" }}>
                                                    {p.gcmtoken ? (
                                                        <button
                                                            onClick={() => { setGcmToken(p.gcmtoken); setTokenCopied(false); }}
                                                            style={{
                                                                background: "none", border: "none",
                                                                color: S.accent, cursor: "pointer",
                                                                fontSize: 12, fontFamily: "monospace",
                                                                padding: 0, textAlign: "left",
                                                            }}
                                                            title="Click to view full token"
                                                        >
                                                            {p.gcmtoken.slice(0, 24)}…
                                                        </button>
                                                    ) : "N/A"}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>
                                    No registered phones found for this organisation.
                                </p>
                            )}
                        </div>

                        {/* Notifications Sent (last 10 days) */}
                        <div style={{ padding: "14px 22px 0" }}>
                            <div style={{ display: "flex", alignItems: "center",
                                justifyContent: "space-between", marginBottom: 10 }}>
                                <p style={{ color: S.muted, fontSize: 11, fontWeight: 700,
                                    textTransform: "uppercase", letterSpacing: "0.07em", margin: 0 }}>
                                    Notifications Sent — Last 10 Days
                                </p>
                                <button
                                    disabled={!pushData.notifications?.length}
                                    onClick={() => {
                                        const headers = ["Phone ID", "OS", "Message", "Status", "Result", "Sent At"];
                                        const rows = pushData.notifications.map((n) => [
                                            n.phoneid, n.os, n.msg,
                                            n.status === "1" ? "Success" : (n.status ?? ""),
                                            n.results, formatPushDate(n.created),
                                        ]);
                                        const csv = [headers, ...rows]
                                            .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
                                            .join("\n");
                                        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = "push_notifications.csv";
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    style={{
                                        backgroundColor: S.accent, color: "#000", border: "none",
                                        padding: "5px 14px", fontSize: 12, fontWeight: 600,
                                        borderRadius: 8, cursor: pushData.notifications?.length ? "pointer" : "not-allowed",
                                        opacity: pushData.notifications?.length ? 1 : 0.4,
                                    }}
                                >
                                    Download CSV
                                </button>
                            </div>
                            {pushData.notifications?.length > 0 ? (
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                                        <thead>
                                        <tr>
                                            {["#", "Phone ID", "OS", "Message", "Status", "Result", "Sent At"].map((h) => (
                                                <TH key={h}>{h}</TH>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {pushData.notifications.map((n, i) => (
                                            <tr key={i}>
                                                <TD><span style={{ color: S.accent, fontSize: 11, fontWeight: 700 }}>{i + 1}</span></TD>
                                                <TD yellow>{n.phoneid}</TD>
                                                <TD>{n.os}</TD>
                                                <td style={{ padding: "11px 16px", color: "#d1d5db",
                                                    borderBottom: "1px solid #1e1e1e", maxWidth: 320,
                                                    whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                                                    {n.msg ?? "N/A"}
                                                </td>
                                                <td style={{ padding: "11px 16px", borderBottom: "1px solid #1e1e1e" }}>
                                                    <span style={{
                                                        display: "inline-block", padding: "2px 10px",
                                                        borderRadius: 8, fontSize: 11, fontWeight: 700,
                                                        backgroundColor: n.status === "1" ? "#14532d" : "#450a0a",
                                                        color:           n.status === "1" ? S.green   : S.red,
                                                    }}>
                                                        {n.status === "1" ? "Success" : n.status ?? "N/A"}
                                                    </span>
                                                </td>
                                                <td style={{ padding: "11px 16px", color: "#9ca3af",
                                                    borderBottom: "1px solid #1e1e1e", maxWidth: 260,
                                                    whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11 }}>
                                                    {n.results ?? "N/A"}
                                                </td>
                                                <TD>{formatPushDate(n.created)}</TD>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p style={{ color: "#6b7280", fontSize: 13, padding: "0 0 14px" }}>
                                    No push notifications found in the last 10 days.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )}

        <button onClick={() => navigate(-1)}
                style={{ marginTop: 8, padding: "9px 22px", borderRadius: 12, fontSize: 13,
                  border: `1px solid ${S.border}`, color: "#aaa",
                  background: "transparent", cursor: "pointer" }}>
          ← Back
        </button>

        {/* Date Range Modal */}
        {showDateModal && (
            <DateRangeModal
                tripsLoading={tripsLoading}
                tripsError={tripsError}
                onCancel={() => { setShowDateModal(false); setTripsError(null); }}
              //  onSubmit={handleFetchTrips}
                onSubmit={(start, end) => {
                    if (chargeMode) {
                        setChargeMode(false);
                        handleFetchCharge(start, end);
                    } else {
                        handleFetchTrips(start, end);
                    }
                }}
            />
        )}

        {/* Trip Detail Drawer */}
        {selectedTrip && (
            <TripDetailDrawer
                trip={selectedTrip}
                vehicleId={vehicleId}
                orgId={orgId}
                onClose={() => setSelectedTrip(null)}
            />
        )}

        {/* GCM Token Modal */}
        {gcmToken && (
            <div
                onClick={() => setGcmToken(null)}
                style={{
                    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.72)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 1000, padding: 24,
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: "#1c1c1c", border: `1px solid ${S.border}`,
                        borderRadius: 16, padding: 28, maxWidth: 640, width: "100%",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginBottom: 16 }}>
                        <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, margin: 0 }}>
                            GCM Token
                        </h3>
                        <button
                            onClick={() => setGcmToken(null)}
                            style={{ background: "none", border: "none", color: "#888",
                                fontSize: 20, cursor: "pointer", lineHeight: 1 }}
                        >
                            ×
                        </button>
                    </div>

                    <div style={{
                        backgroundColor: "#111", border: `1px solid ${S.border}`,
                        borderRadius: 10, padding: "14px 16px", marginBottom: 16,
                        wordBreak: "break-all", fontFamily: "monospace",
                        fontSize: 12, color: "#d1d5db", lineHeight: 1.6,
                    }}>
                        {gcmToken}
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(gcmToken).then(() => {
                                    setTokenCopied(true);
                                    setTimeout(() => setTokenCopied(false), 2000);
                                });
                            }}
                            style={{
                                flex: 1, padding: "9px 0", borderRadius: 10,
                                fontSize: 13, fontWeight: 600, cursor: "pointer",
                                backgroundColor: tokenCopied ? S.green : S.accent,
                                color: "#000", border: "none", transition: "background-color 0.2s",
                            }}
                        >
                            {tokenCopied ? "Copied!" : "Copy Token"}
                        </button>
                        <button
                            onClick={() => setGcmToken(null)}
                            style={{
                                padding: "9px 20px", borderRadius: 10, fontSize: 13,
                                fontWeight: 600, cursor: "pointer", background: "transparent",
                                border: `1px solid ${S.border}`, color: "#aaa",
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        )}

      </Layout>
  );
};

export default DataDiagnoseResultPage;